/**
 * @fileoverview Data Management page component for LENA Admin
 * @description Main page for managing patients, doctors, and pharmacists data
 */
import React, { useRef, useMemo, useCallback } from "react";
import { DmsPage, DmsTabber, DmsAltBox, intl } from "dmsbasereact";
import { Typography, Chip } from "@mui/material";
import { DmsEntityTable } from "../../components/DmsEntityTable";
import { Permissions } from "../../constants/Permissions";
import { calculateAge } from "../../utils/calculateAge";
import { PatientStatusEnum, PatientStatus } from "../../constants/PatientStatusEnum";

/**
 * Data Management page component for LENA Admins
 * Provides centralized management interface for Patients, Doctors, and Pharmacists
 *
 * @returns {JSX.Element} The page component
 */
export const DataManagementPage = () => {
  // Table refs for each entity type
  const patientTableRef = useRef(null);
  const doctorTableRef = useRef(null);
  const pharmacistTableRef = useRef(null);

  // Patient table columns
  const patientColumns = useMemo(() => [
    {
      label: intl.get("PatientID").d("Patient ID"),
      column_name: "patient_identifier",
      filter: true,
      sortable: true,
    },
    {
      label: intl.get("Name").d("Name"),
      column_name: "name",
      filter: true,
      sortable: true,
    },
    {
      label: intl.get("CPF").d("CPF"),
      column_name: "cpf",
      filter: true,
      sortable: true,
    },
    {
      label: intl.get("Age").d("Age"),
      column_name: "birth_date",
      sortable: true,
      function: (value) => calculateAge(value),
    },
    {
      label: intl.get("Status").d("Status"),
      column_name: "status",
      sortable: true,
      element: (row) => (
        <Chip
          label={PatientStatus.getText(row.status)}
          color={PatientStatus.getColor(row.status)}
          size="small"
        />
      ),
    },
  ], []);

  // Doctor table columns
  const doctorColumns = useMemo(() => [
    {
      label: intl.get("Name").d("Name"),
      column_name: "user_name",
      filter: true,
      sortable: true,
    },
    {
      label: intl.get("CPF").d("CPF"),
      column_name: "person_cpf",
      filter: true,
      sortable: true,
    },
    {
      label: intl.get("CRM").d("CRM"),
      column_name: "crm",
      filter: true,
      sortable: true,
    },
    {
      label: intl.get("State").d("State"),
      column_name: "crm_state",
      filter: true,
      sortable: true,
    },
    {
      label: intl.get("Email").d("Email"),
      column_name: "user_email",
      filter: true,
      sortable: true,
    },
    {
      label: intl.get("Status").d("Status"),
      column_name: "status",
      sortable: true,
      element: (row) => (
        <Chip
          label={row.status === 1 ? intl.get("Active").d("Active") : intl.get("Inactive").d("Inactive")}
          color={row.status === 1 ? "success" : "error"}
          size="small"
        />
      ),
    },
  ], []);

  // Pharmacist table columns
  const pharmacistColumns = useMemo(() => [
    {
      label: intl.get("Name").d("Name"),
      column_name: "user_name",
      filter: true,
      sortable: true,
    },
    {
      label: intl.get("CPF").d("CPF"),
      column_name: "person_cpf",
      filter: true,
      sortable: true,
    },
    {
      label: intl.get("CRF").d("CRF"),
      column_name: "crf",
      filter: true,
      sortable: true,
    },
    {
      label: intl.get("State").d("State"),
      column_name: "crf_state",
      filter: true,
      sortable: true,
    },
    {
      label: intl.get("Email").d("Email"),
      column_name: "user_email",
      filter: true,
      sortable: true,
    },
    {
      label: intl.get("Status").d("Status"),
      column_name: "status",
      sortable: true,
      element: (row) => (
        <Chip
          label={row.status === 1 ? intl.get("Active").d("Active") : intl.get("Inactive").d("Inactive")}
          color={row.status === 1 ? "success" : "error"}
          size="small"
        />
      ),
    },
  ], []);

  // Status check functions
  const isPatientActive = useCallback((row) => row.status === PatientStatusEnum.ACTIVE, []);
  const isDoctorActive = useCallback((row) => row.status === 1, []);
  const isPharmacistActive = useCallback((row) => row.status === 1, []);

  // Placeholder handlers for edit actions
  const handleViewPatient = useCallback((row) => {
    // TODO: Implement patient detail modal
  }, []);

  const handleViewDoctor = useCallback((row) => {
    // TODO: Implement doctor detail modal
  }, []);

  const handleViewPharmacist = useCallback((row) => {
    // TODO: Implement pharmacist detail modal
  }, []);

  // Tab definitions
  const tabs = [
    {
      title: intl.get("Patients").d("Patients"),
      component: (
        <DmsEntityTable
          ref={patientTableRef}
          url="patient/table"
          columns={patientColumns}
          onView={handleViewPatient}
          isActiveCheck={isPatientActive}
          viewTitle={intl.get("ViewPatientDetails").d("Ver Detalhes do Paciente")}
        />
      ),
      permissions: [Permissions.LENA_ADMIN_ACCESS]
    },
    {
      title: intl.get("Doctors").d("Doctors"),
      component: (
        <DmsEntityTable
          ref={doctorTableRef}
          url="doctor/table"
          columns={doctorColumns}
          onView={handleViewDoctor}
          isActiveCheck={isDoctorActive}
          viewTitle={intl.get("ViewDoctorDetails").d("View Doctor Details")}
        />
      ),
      permissions: [Permissions.LENA_ADMIN_ACCESS]
    },
    {
      title: intl.get("Pharmacists").d("Pharmacists"),
      component: (
        <DmsEntityTable
          ref={pharmacistTableRef}
          url="pharmacist/table"
          columns={pharmacistColumns}
          onView={handleViewPharmacist}
          isActiveCheck={isPharmacistActive}
          viewTitle={intl.get("ViewPharmacistDetails").d("View Pharmacist Details")}
        />
      ),
      permissions: [Permissions.LENA_ADMIN_ACCESS]
    },
  ];

  return (
    <DmsPage
      name={intl.get("DataManagement").d("Data Management")}
      manual_slug="data-management"
      permissions={[Permissions.LENA_ADMIN_ACCESS]}
    >
      <DmsAltBox.Card>
        <DmsAltBox.Header>
          <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
            {intl.get("DataManagement").d("Data Management")}
          </Typography>
        </DmsAltBox.Header>
        <DmsAltBox.Body>
          <DmsTabber tabs={tabs} pageName={intl.get("DataManagement").d("Data Management")} />
        </DmsAltBox.Body>
      </DmsAltBox.Card>
    </DmsPage>
  );
};
